// Status de foto por variante — e, em produtos sem variantes, no nível do produto.
// FOTO_PENDENTE: só existe imagem provisória (IA/referência).
// FOTO_EM_REVISAO: foto real enviada, aguardando revisão/padronização/confirmação.
// FOTO_APROVADA: foto real revisada e aprovada — imagem principal pública.
// Etapa 3: as imagens vivem no armazenamento durável do servidor (URLs estáveis /api/files/...); o MongoDB
// guarda apenas referências/metadados. Nada de base64 permanente nem localStorage.
import { useEffect, useState } from 'react';
import { getAllProducts } from '../data/catalog';
import { patchCustomVariant, updateCustomProduct, getCatalogPhotoSettings, setCatalogPhotoSettings, refreshCatalog } from './customProducts';

const API = process.env.REACT_APP_BACKEND_URL;
const EVENT = 'hud-photos-changed';

export const PHOTO_STATUS = { PENDENTE: 'FOTO_PENDENTE', REVISAO: 'FOTO_EM_REVISAO', APROVADA: 'FOTO_APROVADA' };
export const PHOTO_STATUS_LABEL = {
  FOTO_PENDENTE: 'Foto pendente',
  FOTO_EM_REVISAO: 'Foto em revisão',
  FOTO_APROVADA: 'Foto aprovada',
};

let historyCache = [];
let ready = false;
let initPromise = null;

const staffRole = () => {
  try {
    return JSON.parse(localStorage.getItem('hud_admin_user') || 'null')?.role || '';
  } catch {
    return '';
  }
};

// Histórico de fotos é interno (painel); sem sessão de staff mantém cache vazio.
export function initPhotos(force = false) {
  if (initPromise && !force) return initPromise;
  initPromise = (async () => {
    try {
      const role = staffRole();
      if (role === 'admin' || role === 'vendedor') {
        const res = await fetch(`${API}/api/photos/history`, { headers: { 'X-Admin-Role': role } });
        if (res.ok) historyCache = await res.json();
      }
      ready = true;
      notifyPhotosChanged();
    } catch (e) {
      console.error('Fotos: falha ao carregar histórico do servidor.', e);
    }
  })();
  return initPromise;
}

export const isPhotosReady = () => ready;

export const getPhotoStatus = (v) => v.photoStatus || PHOTO_STATUS.APROVADA;
// Produto sem variantes: status da foto pertence ao próprio produto (sem variante fictícia).
export const getProductPhotoStatus = (p) => p.photoStatus || PHOTO_STATUS.APROVADA;

export function getPhotoSettings() {
  return getCatalogPhotoSettings();
}

export function getPhotoHistory() {
  return historyCache;
}

export function recordPhotoEvent(entry) {
  pushPhotoHistory(entry);
}

function pushPhotoHistory(entry) {
  historyCache = [...historyCache, entry];
  const role = staffRole();
  if (role !== 'admin' && role !== 'vendedor') return;
  fetch(`${API}/api/photos/history`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Admin-Role': role },
    body: JSON.stringify(entry),
  }).catch((e) => console.error('Fotos: falha ao gravar histórico.', e));
}

export function notifyPhotosChanged() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVENT));
}

export function usePhotoTick() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const h = () => setTick((t) => t + 1);
    window.addEventListener(EVENT, h);
    return () => window.removeEventListener(EVENT, h);
  }, []);
}

// Preferências de fotos (imagem provisória, revisão foto a foto) gravadas no servidor — admin only.
// Faz merge das chaves: salvar uma preferência nunca apaga as outras.
export function savePhotoSettings(s, user) {
  const next = { ...getCatalogPhotoSettings(), ...s };
  setCatalogPhotoSettings(next);
  if (s.allowProvisional !== undefined) {
    pushPhotoHistory({
      at: new Date().toISOString(),
      userId: user?.id,
      userName: user?.name,
      action: s.allowProvisional
        ? 'Permitiu imagens provisórias no site público.'
        : 'Passou a exibir "Foto em atualização" no site público.',
      origem: 'FOTOS',
    });
  }
  fetch(`${API}/api/review/config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'X-Admin-Role': staffRole() },
    body: JSON.stringify({ allowProvisional: !!next.allowProvisional, reviewEach: !!next.reviewEach }),
  }).catch((e) => console.error('Fotos: falha ao gravar preferência.', e));
  notifyPhotosChanged();
}

// Regra pública: foto aprovada → usa; troca em revisão → mantém a ÚLTIMA aprovada no ar;
// pendente → provisória só se o admin permitir, caso contrário placeholder "Foto em atualização" (null).
export function resolveVariantImage(v) {
  const s = getPhotoStatus(v);
  if (s === PHOTO_STATUS.APROVADA) return v.image;
  if (s === PHOTO_STATUS.REVISAO && v.photoApproved) return v.photoApproved;
  return getPhotoSettings().allowProvisional ? v.image : null;
}

// Mesma regra no nível do produto (produtos sem variantes).
export function resolveProductImage(p) {
  const s = getProductPhotoStatus(p);
  if (s === PHOTO_STATUS.APROVADA) return p.image || null;
  if (s === PHOTO_STATUS.REVISAO && p.photoApproved) return p.photoApproved;
  return getPhotoSettings().allowProvisional ? p.image || null : null;
}

// Origem da imagem pública: 'original'/'padronizada' são a foto REAL (padronização é só
// recorte determinístico); 'estilizada' foi reapresentada por IA. Pendente exibindo
// imagem provisória (IA/referência) também é ilustrativa. O selo é só sobreposição visual.
export function isIllustrativeVariantImage(v) {
  if (!v) return false;
  if (getPhotoStatus(v) === PHOTO_STATUS.PENDENTE) return !!v.image;
  return v.photoKind === 'estilizada';
}

export function isIllustrativeProductImage(p, shownSrc) {
  if (!p) return false;
  if (shownSrc && p.coverImage && shownSrc === p.coverImage) return true;
  if (getProductPhotoStatus(p) === PHOTO_STATUS.PENDENTE) return !!p.image;
  return p.photoKind === 'estilizada';
}

export function listPhotoEntries() {
  return getAllProducts().flatMap((p) =>
    p.hasVariants === false
      ? [{ product: p, variant: null, status: getProductPhotoStatus(p) }]
      : p.variants.map((v) => ({ product: p, variant: v, status: getPhotoStatus(v) }))
  );
}

export const listPendingPhotos = () => listPhotoEntries().filter((e) => e.status !== PHOTO_STATUS.APROVADA);

// Envia uma imagem (data URL/Blob) ao armazenamento durável do servidor via backend e devolve a URL estável.
export async function uploadImage(dataUrl, scope = 'catalogo') {
  const blob = await (await fetch(dataUrl)).blob();
  const fd = new FormData();
  fd.append('file', blob, 'foto.jpg');
  const res = await fetch(`${API}/api/uploads/image?scope=${encodeURIComponent(scope)}`, {
    method: 'POST',
    headers: { 'X-Admin-Role': staffRole() },
    body: fd,
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.detail || 'Falha ao enviar a foto.');
  return d.url;
}

// Remove do storage um arquivo recusado (marca is_deleted e apaga do disco) — admin only;
// falha aqui não bloqueia o fluxo (o pior caso é um arquivo órfão).
export async function deleteUploadedImage(url) {
  if (typeof url !== 'string' || !url.startsWith('/api/files/')) return;
  try {
    await fetch(`${API}/api/uploads/file`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'X-Admin-Role': staffRole() },
      body: JSON.stringify({ url }),
    });
  } catch (e) {
    console.error('Fotos: falha ao remover arquivo recusado.', e);
  }
}

// Qualquer campo de imagem ainda em data URL sobe para o storage antes de gravar a referência.
async function uploadPatchImages(patch, scope) {
  const out = { ...patch };
  for (const f of ['image', 'photoApproved', 'photoThumb', 'photoOriginal', 'coverImage']) {
    if (typeof out[f] === 'string' && out[f].startsWith('data:')) {
      out[f] = await uploadImage(out[f], scope);
    }
  }
  return out;
}

// Foto de variante (produto com variantes).
export async function patchVariantPhoto(product, variantId, patch, user, action) {
  const finalPatch = await uploadPatchImages(patch, `variantes/${product.slug}`);
  await patchCustomVariant(product.slug, variantId, finalPatch);
  pushPhotoHistory({
    at: new Date().toISOString(),
    userId: user?.id,
    userName: user?.name,
    action,
    produto: product.name,
    variante: variantId,
    origem: 'FOTOS',
  });
  notifyPhotosChanged();
}

// Foto no nível do produto (produto sem variantes — nunca uma variante fictícia).
export async function patchProductPhoto(product, patch, user, action) {
  const finalPatch = await uploadPatchImages(patch, `produtos/${product.slug}`);
  await updateCustomProduct(product.slug, finalPatch);
  pushPhotoHistory({
    at: new Date().toISOString(),
    userId: user?.id,
    userName: user?.name,
    action,
    produto: product.name,
    variante: null,
    origem: 'FOTOS',
  });
  notifyPhotosChanged();
}

// Envio de foto pelo VENDEDOR: sobe a imagem ao storage e grava via endpoint de staff que
// aceita SOMENTE photoOriginal + status "Em revisão" — nenhum outro campo do catálogo.
// Depois recarrega o catálogo do servidor para o painel refletir na hora.
export async function submitPhotoForReview(product, variantId, dataUrl, user, action) {
  const url = await uploadImage(dataUrl, variantId ? `variantes/${product.slug}` : `produtos/${product.slug}`);
  const res = await fetch(`${API}/api/photos/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Admin-Role': staffRole() },
    body: JSON.stringify({ slug: product.slug, variantId: variantId || null, photoOriginal: url }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.detail || 'Falha ao enviar a foto.');
  await refreshCatalog();
  pushPhotoHistory({
    at: new Date().toISOString(),
    userId: user?.id,
    userName: user?.name,
    action,
    produto: product.name,
    variante: variantId || null,
    origem: 'FOTOS',
  });
  notifyPhotosChanged();
}

// Padronização SIMULADA: somente enquadramento/centralização em fundo neutro,
// correção de proporção, compressão e redimensionamento + thumbnail.
// NUNCA altera cor, tonalidade, estampa, textura ou brilho do tecido.
export function mockStandardizePhoto(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const make = (size) => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#f3efe9';
        ctx.fillRect(0, 0, size, size);
        const margin = Math.round(size * 0.06);
        const scale = Math.min((size - margin * 2) / img.width, (size - margin * 2) / img.height);
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        ctx.drawImage(img, Math.round((size - w) / 2), Math.round((size - h) / 2), w, h);
        return canvas.toDataURL('image/jpeg', 0.85);
      };
      resolve({ standard: make(800), thumb: make(320) });
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

// Padronização REAL via backend: a IA de visão localiza a região do tecido e o
// servidor recorta de forma determinística (cor/estampa/textura preservadas).
// `ref` aceita data URL (foto recém-enviada) ou URL /api/files/... (já no storage).
export async function standardizePhotoAI(ref, role) {
  const res = await fetch(`${API}/api/ai/standardize-photo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Admin-Role': role || staffRole() },
    body: JSON.stringify({ photo: ref }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.detail || 'Padronização por IA indisponível.');
  return { ...d, ai: true };
}

// Estilização por IA: reapresentação editorial (tecido drapeado) a partir da foto
// real como referência. A imagem é REGENERADA — a fidelidade de cor/estampa/textura
// é conferida pelo admin na revisão antes de aprovar. Retorna {styled, thumb} dataURLs.
export async function stylizePhotoAI(ref, role, productName, variantName) {
  const res = await fetch(`${API}/api/ai/stylize-photo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Admin-Role': role || staffRole() },
    body: JSON.stringify({ photo: ref, productName: productName || '', variantName: variantName || '' }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.detail || 'Estilização por IA indisponível.');
  return { standard: d.styled, thumb: d.thumb };
}
